import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { SessionService } from '@app/core/services/session/session.service';
import { Division } from '@app/features/division/models/division.interface';
import { DivisionService } from '@app/features/division/services/division.service';
import { DefaultMenuEntry } from '@app/features/menu/models/default-menu-entry.model';
import { Menu } from '@app/features/menu/models/menu.model';
import { Platform } from '@app/shared/constants';
import { MenuItem } from '@app/shared/models/menu.interface';
import { Page } from '@app/shared/models/page.interface';
import { SearchParams } from '@app/shared/models/search-params.model';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  constructor(
    private http: HttpClient,
    protected divisionService: DivisionService,
    private sessionService: SessionService,
    private appConfigService: AppConfigService
  ) {}

  getMenuForLoggedInUser(): Observable<MenuItem[]> {
    return this.http
      .get<MenuItem[]>(`${this.appConfigService.getApi()}/menus/user?lang=${this.sessionService.getLang()}`)
      .pipe(catchError(_ => of([])));
  }

  getById(id: string): Observable<Menu> {
    return this.http.get<Menu>(`${this.appConfigService.getApi()}/menus/${id}`);
  }

  getNew(platfrm: Platform): Observable<Menu> {
    return this.divisionService.getAll().pipe(
      map((divisions: Division[]) => {
        const menu: Menu = {
          id: null,
          name: '',
          platform: platfrm,
          defaultMenu: false,
          menuProfiles: [],
          menuUsers: [],
          menuDivisions: [],
          entries: []
        } as Menu;
        if (divisions.length === 1) {
          menu.menuDivisions.push({ id: null, menu: null, division: divisions[0] });
        }
        return menu;
      })
    );
  }

  getByMenuName(name: string): Observable<Menu> {
    return this.http.get<Menu>(`${this.appConfigService.getApi()}/public/menus/${name}`);
  }

  getPage(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string
  ): Observable<Page<Menu>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };

    return this.http.post<Page<Menu>>(`${this.appConfigService.getApi()}/menus/search`, params);
  }

  getDefaultEntries(platform: Platform): Observable<DefaultMenuEntry[]> {
    const params = new HttpParams().append('platform', platform);

    return this.http.get<DefaultMenuEntry[]>(`${this.appConfigService.getApi()}/menus/default-entries`, { params });
  }

  save(menu: Menu): Observable<Menu> {
    return menu.id != null ? this.update(menu.id, menu) : this.create(menu);
  }

  create(menu: Menu): Observable<Menu> {
    return this.http.post<Menu>(`${this.appConfigService.getApi()}/menus`, menu);
  }

  update(id: string, menu: Menu): Observable<Menu> {
    return this.http.put<Menu>(`${this.appConfigService.getApi()}/menus/${id}`, menu);
  }

  copy(menus: string[]): Observable<Menu> {
    return this.http.post<Menu>(`${this.appConfigService.getApi()}/menus/copy`, menus);
  }

  delete(menus: string[]): Observable<Menu> {
    return this.http.post<Menu>(`${this.appConfigService.getApi()}/menus/delete`, menus);
  }
}
